import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Server, Plus, Trash2, Edit2, LogOut, Shield, Check, X, Globe, Key, AlertCircle, CheckCircle2, Type, Image } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = ({ onLicenseChange }) => {
  const navigate = useNavigate();
  const { appName: currentAppName, refresh: refreshSettings, colorFrom: currentColorFrom, colorTo: currentColorTo } = useSettings();
  const [servers, setServers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [error, setError] = useState('');

  // License state
  const [license, setLicense] = useState(null);
  const [licenseKey, setLicenseKey] = useState('');
  const [licenseDomain, setLicenseDomain] = useState(window.location.hostname);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseError, setLicenseError] = useState('');

  // App name state
  const [appNameInput, setAppNameInput] = useState(currentAppName);
  const [nameSaved, setNameSaved] = useState(false);

  // Login bg state
  const [bgUrl, setBgUrl] = useState('');
  const [bgSaved, setBgSaved] = useState(false);

  // Color state
  const [colFrom, setColFrom] = useState(currentColorFrom);
  const [colTo, setColTo] = useState(currentColorTo);
  const [colorSaved, setColorSaved] = useState(false);

  // Accent color state
  const [accent, setAccent] = useState('#a855f7');
  const [accentSaved, setAccentSaved] = useState(false);

  const getCreds = () => {
    try { return JSON.parse(localStorage.getItem('admin_creds') || '{}'); } catch { return {}; }
  };

  const buildParams = () => {
    const { username, password } = getCreds();
    return new URLSearchParams({ admin_user: username, admin_pass: password });
  };

  const fetchServers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/servers?${buildParams()}`);
      if (!res.ok) { navigate('/login'); return; }
      setServers(await res.json());
    } catch { navigate('/login'); }
  };

  const fetchLicense = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/license?${buildParams()}`);
      if (res.ok) {
        const data = await res.json();
        setLicense(data.status === 'active' ? data : null);
      }
    } catch {}
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/settings?${buildParams()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.login_bg) setBgUrl(data.login_bg);
        if (data.color_from) setColFrom(data.color_from);
        if (data.color_to) setColTo(data.color_to);
        if (data.accent_color) setAccent(data.accent_color);
      }
    } catch {}
  };

  useEffect(() => {
    const creds = getCreds();
    if (!creds.username) { navigate('/login'); return; }
    Promise.all([fetchServers(), fetchLicense(), fetchSettings()]).then(() => setIsLoading(false));
  }, []);

  // Server handlers
  const handleAdd = async () => {
    if (!newName.trim() || !newUrl.trim()) { setError('Name and URL required'); return; }
    setError('');
    const res = await fetch(`${BACKEND_URL}/api/admin/servers?${buildParams()}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName.trim(), url: newUrl.trim() })
    });
    if (res.ok) { setServers((await res.json()).servers); setNewName(''); setNewUrl(''); }
  };

  const handleDelete = async (url) => {
    const params = buildParams(); params.append('url', url);
    const res = await fetch(`${BACKEND_URL}/api/admin/servers?${params}`, { method: 'DELETE' });
    if (res.ok) setServers((await res.json()).servers);
  };

  const handleUpdate = async (oldUrl) => {
    if (!editName.trim() || !editUrl.trim()) return;
    const params = buildParams();
    params.append('old_url', oldUrl); params.append('name', editName.trim()); params.append('new_url', editUrl.trim());
    const res = await fetch(`${BACKEND_URL}/api/admin/servers?${params}`, { method: 'PUT' });
    if (res.ok) { setServers((await res.json()).servers); setEditIdx(null); }
  };

  // License handlers
  const handleActivate = async () => {
    if (!licenseKey.trim()) { setLicenseError('License key required'); return; }
    setLicenseError(''); setLicenseLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/license?${buildParams()}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ license_key: licenseKey.trim(), domain: licenseDomain })
      });
      const data = await res.json();
      if (data.valid) {
        setLicense(data.license);
        setLicenseKey('');
        setLicenseError('');
        onLicenseChange?.();
      } else {
        setLicenseError(data.error || 'License verification failed');
      }
    } catch (e) {
      setLicenseError('Could not reach license server');
    }
    setLicenseLoading(false);
  };

  const handleDeactivate = async () => {
    await fetch(`${BACKEND_URL}/api/admin/license?${buildParams()}`, { method: 'DELETE' });
    setLicense(null);
    onLicenseChange?.();
  };

  const handleSaveName = async () => {
    if (!appNameInput.trim()) return;
    const params = buildParams();
    params.append('app_name', appNameInput.trim());
    await fetch(`${BACKEND_URL}/api/admin/settings?${params}`, { method: 'PUT' });
    refreshSettings();
    setNameSaved(true);
    setTimeout(() => setNameSaved(false), 2000);
  };

  const handleSaveBg = async () => {
    const params = buildParams();
    params.append('login_bg', bgUrl.trim());
    await fetch(`${BACKEND_URL}/api/admin/settings?${params}`, { method: 'PUT' });
    refreshSettings();
    setBgSaved(true);
    setTimeout(() => setBgSaved(false), 2000);
  };

  const handleSaveColors = async () => {
    const params = buildParams();
    params.append('color_from', colFrom);
    params.append('color_to', colTo);
    await fetch(`${BACKEND_URL}/api/admin/settings?${params}`, { method: 'PUT' });
    refreshSettings();
    setColorSaved(true);
    setTimeout(() => setColorSaved(false), 2000);
  };

  const handleSaveAccent = async () => {
    const params = buildParams();
    params.append('accent_color', accent);
    await fetch(`${BACKEND_URL}/api/admin/settings?${params}`, { method: 'PUT' });
    refreshSettings();
    setAccentSaved(true);
    setTimeout(() => setAccentSaved(false), 2000);
  };

  if (isLoading) {
    return <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center"><div className="text-gray-400">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Header */}
      <div className="bg-[#0d0d0f] border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-purple-500" />
            <h1 className="text-xl font-bold">Admin Panel</h1>
          </div>
          <button onClick={() => { localStorage.removeItem('admin_creds'); navigate('/login'); }}
            className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-10">

        {/* App Name */}
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-6">
            <Type className="w-5 h-5 text-purple-500" />
            App Name
          </h2>
          <div className="bg-[#141417] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-3">
              <Input value={appNameInput} onChange={e => setAppNameInput(e.target.value)}
                placeholder="App name"
                className="bg-[#1a1a1d] border-gray-700 text-white flex-1" />
              <Button onClick={handleSaveName} disabled={!appNameInput.trim() || appNameInput === currentAppName}
                className="bg-purple-600 hover:bg-purple-700 px-6">
                {nameSaved ? <><Check className="w-4 h-4 mr-1" /> Saved</> : 'Save'}
              </Button>
            </div>
            <p className="text-gray-500 text-xs mt-2">This name appears on the login page and navigation bar.</p>
          </div>
        </div>

        {/* Name Colors */}
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-6">
            <Type className="w-5 h-5 text-purple-500" />
            Name Colors
          </h2>
          <div className="bg-[#141417] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-3">
                <label className="text-gray-400 text-sm">From</label>
                <div className="relative">
                  <input type="color" value={colFrom} onChange={e => setColFrom(e.target.value)}
                    className="w-10 h-10 rounded-lg border border-gray-600 cursor-pointer bg-transparent" />
                </div>
                <input type="text" value={colFrom} onChange={e => setColFrom(e.target.value)}
                  className="w-24 px-2 py-1 bg-[#1a1a1d] border border-gray-700 rounded text-white text-xs font-mono" />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-gray-400 text-sm">To</label>
                <div className="relative">
                  <input type="color" value={colTo} onChange={e => setColTo(e.target.value)}
                    className="w-10 h-10 rounded-lg border border-gray-600 cursor-pointer bg-transparent" />
                </div>
                <input type="text" value={colTo} onChange={e => setColTo(e.target.value)}
                  className="w-24 px-2 py-1 bg-[#1a1a1d] border border-gray-700 rounded text-white text-xs font-mono" />
              </div>
              <Button onClick={handleSaveColors} className="bg-purple-600 hover:bg-purple-700 px-6">
                {colorSaved ? <><Check className="w-4 h-4 mr-1" /> Saved</> : 'Save'}
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-500 text-sm">Preview:</span>
              <span className="text-2xl font-bold bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(to right, ${colFrom}, ${colTo})` }}>
                {appNameInput || 'Synapse'}
              </span>
            </div>
          </div>
        </div>

        {/* Accent / Highlight Color */}
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-6">
            <Type className="w-5 h-5 text-purple-500" />
            Highlight Color
          </h2>
          <div className="bg-[#141417] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-4 mb-4">
              <input type="color" value={accent} onChange={e => setAccent(e.target.value)}
                className="w-10 h-10 rounded-lg border border-gray-600 cursor-pointer bg-transparent" />
              <input type="text" value={accent} onChange={e => setAccent(e.target.value)}
                className="w-28 px-2 py-1 bg-[#1a1a1d] border border-gray-700 rounded text-white text-xs font-mono" />
              <Button onClick={handleSaveAccent} className="bg-purple-600 hover:bg-purple-700 px-6">
                {accentSaved ? <><Check className="w-4 h-4 mr-1" /> Saved</> : 'Save'}
              </Button>
            </div>
            <p className="text-gray-500 text-xs mb-3">Used for selected items, active tabs, progress bars, EPG highlights, and buttons.</p>
            <div className="flex items-center gap-3">
              <span className="text-gray-500 text-sm">Preview:</span>
              <span className="px-4 py-1.5 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: accent }}>Selected</span>
              <span className="px-4 py-1.5 rounded-lg text-sm font-medium border" style={{ borderColor: accent, color: accent }}>Outline</span>
              <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: '60%', backgroundColor: accent }} />
              </div>
            </div>
          </div>
        </div>

        {/* Login Background */}
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-6">
            <Image className="w-5 h-5 text-purple-500" />
            Login Background
          </h2>
          <div className="bg-[#141417] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-3 mb-3">
              <Input value={bgUrl} onChange={e => setBgUrl(e.target.value)}
                placeholder="Image URL (e.g. https://example.com/bg.jpg)"
                className="bg-[#1a1a1d] border-gray-700 text-white flex-1" />
              <Button onClick={handleSaveBg}
                className="bg-purple-600 hover:bg-purple-700 px-6">
                {bgSaved ? <><Check className="w-4 h-4 mr-1" /> Saved</> : 'Save'}
              </Button>
            </div>
            {bgUrl && (
              <div className="mt-3 rounded-lg overflow-hidden border border-gray-700 h-32 relative">
                <img src={bgUrl} alt="Preview" className="w-full h-full object-cover opacity-50"
                  onError={(e) => { e.target.style.display = 'none'; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <p className="absolute bottom-2 left-3 text-white text-xs">Preview</p>
              </div>
            )}
            <p className="text-gray-500 text-xs mt-2">Enter an image URL for the login page background. Leave empty for no background.</p>
          </div>
        </div>

        {/* License Section */}
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-6">
            <Key className="w-5 h-5 text-purple-500" />
            License
          </h2>

          {license ? (
            <div className="bg-[#141417] rounded-xl p-5 border border-green-800/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold">License Active</p>
                    <p className="text-gray-400 text-sm mt-1">{license.product_name}</p>
                  </div>
                </div>
                <button onClick={handleDeactivate}
                  className="text-xs text-gray-500 hover:text-red-400 transition-colors">Deactivate</button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">License Key</p>
                  <p className="text-gray-300 font-mono text-xs mt-0.5">{license.license_key}</p>
                </div>
                <div>
                  <p className="text-gray-500">Domain</p>
                  <p className="text-gray-300 text-xs mt-0.5">{license.domain}</p>
                </div>
                <div>
                  <p className="text-gray-500">Expires</p>
                  <p className="text-gray-300 text-xs mt-0.5">
                    {license.expires_at ? new Date(license.expires_at).toLocaleDateString() : 'Never'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Verified</p>
                  <p className="text-gray-300 text-xs mt-0.5">
                    {license.verified_at ? new Date(license.verified_at).toLocaleString() : '-'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-[#141417] rounded-xl p-5 border border-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                <p className="text-gray-400 text-sm">No active license. Enter your license key to activate.</p>
              </div>
              <div className="flex items-center gap-3">
                <Input value={licenseKey} onChange={e => { setLicenseKey(e.target.value); setLicenseError(''); }}
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  className="bg-[#1a1a1d] border-gray-700 text-white font-mono flex-1" />
                <Input value={licenseDomain} onChange={e => setLicenseDomain(e.target.value)}
                  placeholder="your-domain.com"
                  className="bg-[#1a1a1d] border-gray-700 text-white w-48" />
                <Button onClick={handleActivate} disabled={licenseLoading}
                  className="bg-purple-600 hover:bg-purple-700 px-6">
                  {licenseLoading ? 'Verifying...' : 'Activate'}
                </Button>
              </div>
              {licenseError && (
                <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {licenseError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Server Management */}
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-6">
            <Server className="w-5 h-5 text-purple-500" />
            Xtream Servers ({servers.length})
          </h2>

          <div className="space-y-3 mb-6">
            {servers.map((s, i) => (
              <div key={i} className="bg-[#141417] rounded-xl p-4 border border-gray-800">
                {editIdx === i ? (
                  <div className="flex items-center gap-3">
                    <Input value={editName} onChange={e => setEditName(e.target.value)}
                      placeholder="Server name" className="bg-[#1a1a1d] border-gray-700 text-white flex-1" />
                    <Input value={editUrl} onChange={e => setEditUrl(e.target.value)}
                      placeholder="Server URL" className="bg-[#1a1a1d] border-gray-700 text-white flex-[2]" />
                    <button onClick={() => handleUpdate(s.url)}
                      className="p-2 text-green-400 hover:bg-green-400/10 rounded-lg"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditIdx(null)}
                      className="p-2 text-gray-400 hover:bg-gray-700 rounded-lg"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <Globe className="w-5 h-5 text-purple-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white font-medium">{s.name}</p>
                        <p className="text-gray-500 text-sm truncate">{s.url}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => { setEditIdx(i); setEditName(s.name); setEditUrl(s.url); }}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(s.url)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {servers.length === 0 && (
              <div className="text-center py-8 text-gray-500">No servers configured</div>
            )}
          </div>

          <div className="bg-[#141417] rounded-xl p-4 border border-gray-800 border-dashed">
            <p className="text-sm text-gray-400 mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add New Server
            </p>
            <div className="flex items-center gap-3">
              <Input value={newName} onChange={e => { setNewName(e.target.value); setError(''); }}
                placeholder="Server name (e.g. Main Server)"
                className="bg-[#1a1a1d] border-gray-700 text-white flex-1" />
              <Input value={newUrl} onChange={e => { setNewUrl(e.target.value); setError(''); }}
                placeholder="Server URL (e.g. https://server.com:443)"
                className="bg-[#1a1a1d] border-gray-700 text-white flex-[2]" />
              <Button onClick={handleAdd} className="bg-purple-600 hover:bg-purple-700 px-6">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
